// src/admin/pages/ActivityLogsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Filter, Download, RefreshCw, Calendar, User, Building, Folder, File } from 'lucide-react';
import activityLogService from '../../services/activityLogService';
import { supabase } from '../../config';
import { getUserDisplayName } from '../../services/utilityService';

const ActivityLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'equity', 'dataroom'
  const [limit, setLimit] = useState(100);
  const [users, setUsers] = useState({});
  const [companies, setCompanies] = useState({});

  const loadUserNames = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, username');
      
      if (data) {
        const userMap = {};
        data.forEach(user => {
          userMap[user.id] = getUserDisplayName(user, 'Unknown');
        });
        setUsers(userMap);
      }
    } catch (error) {
      console.error('Error loading user names:', error);
    }
  }, []);

  const loadDataroomLogs = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('dataroom_logs')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map(log => ({
        ...log,
        module: 'dataroom',
        timestamp: log.timestamp || log.created_at
      }));
    } catch (error) {
      console.error('Error loading dataroom logs:', error);
      return [];
    }
  }, [limit]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      if (filter === 'equity' || filter === 'all') {
        const equityResult = await activityLogService.getEquityLogs(null, limit);
        if (equityResult.success && equityResult.data) {
          const equityLogs = equityResult.data.map(log => ({
            ...log,
            module: 'equity',
            timestamp: log.timestamp || log.created_at
          }));
          
          // Load company names for equity logs
          const companyIds = [...new Set(equityLogs.map(l => l.company_id).filter(Boolean))];
          if (companyIds.length > 0) {
            const { data: companyData } = await supabase
              .from('equity_companies')
              .select('id, name')
              .in('id', companyIds);
            
            if (companyData) {
              const companyMap = {};
              companyData.forEach(company => {
                companyMap[company.id] = company.name;
              });
              setCompanies(companyMap);
            }
          }

          if (filter === 'all') {
            // Load dataroom logs too
            const dataroomResult = await loadDataroomLogs();
            setLogs([...equityLogs, ...dataroomResult].sort((a, b) => 
              new Date(b.timestamp) - new Date(a.timestamp)
            ));
          } else {
            setLogs(equityLogs);
          }
        }
      } else if (filter === 'dataroom') {
        const dataroomLogs = await loadDataroomLogs();
        setLogs(dataroomLogs);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, limit, loadDataroomLogs]);

  useEffect(() => {
    loadLogs();
    loadUserNames();
  }, [filter, limit, loadLogs, loadUserNames]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'N/A';
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } catch {
      return 'N/A';
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('login') || action.includes('logout')) return <User className="w-4 h-4" />;
    if (action.includes('company') || action.includes('project')) return <Building className="w-4 h-4" />;
    if (action.includes('folder')) return <Folder className="w-4 h-4" />;
    if (action.includes('file')) return <File className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const getModuleBadge = (module) => {
    const colors = {
      equity: 'bg-blue-100 text-blue-800',
      dataroom: 'bg-green-100 text-green-800'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[module] || 'bg-gray-100 text-gray-800'}`}>
        {module?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const exportLogs = () => {
    const csv = [
      ['Timestamp', 'Module', 'User', 'Action', 'Company/Project', 'Details'].join(','),
      ...logs.map(log => [
        formatTimestamp(log.timestamp),
        log.module || 'unknown',
        users[log.user_id] || log.user_id || 'Unknown',
        log.action || 'N/A',
        log.company_id ? companies[log.company_id] || log.company_id : log.project_id || 'N/A',
        JSON.stringify(log.details || {}).replace(/"/g, '""')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="w-full max-w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Logs</h1>
          <p className="text-sm text-gray-600 mt-1">
            View user activity across Equity and Dataroom modules
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportLogs}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={loadLogs}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow">
        <Filter className="w-5 h-5 text-gray-500" />
        <div className="flex gap-2">
          {['all', 'equity', 'dataroom'].map(option => (
            <button
              key={option}
              onClick={() => setFilter(option)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === option
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="ml-auto px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value={50}>50 logs</option>
          <option value={100}>100 logs</option>
          <option value={250}>250 logs</option>
          <option value={500}>500 logs</option>
        </select>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading activity logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No activity logs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto w-full max-w-full">
            <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Module
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Context
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={`${log.module}-${log.id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatTimestamp(log.timestamp)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getModuleBadge(log.module)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {users[log.user_id] || log.user_id?.substring(0, 8) || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm">
                        {getActionIcon(log.action)}
                        <span className="font-medium">{log.action || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {log.company_id && companies[log.company_id] 
                        ? `${companies[log.company_id]} (Company)` 
                        : log.project_id 
                        ? `Project ${log.project_id}` 
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {log.details && Object.keys(log.details).length > 0 ? (
                        <details className="cursor-pointer">
                          <summary className="text-blue-600 hover:text-blue-800">
                            View details
                          </summary>
                          <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-auto max-w-md">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </details>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stats */}
      {!loading && logs.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-blue-600 font-medium">Total Logs</div>
            <div className="text-2xl font-bold text-blue-900">{logs.length}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-600 font-medium">Dataroom</div>
            <div className="text-2xl font-bold text-green-900">
              {logs.filter(l => l.module === 'dataroom').length}
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-sm text-purple-600 font-medium">Equity</div>
            <div className="text-2xl font-bold text-purple-900">
              {logs.filter(l => l.module === 'equity').length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogsPage;




